// KMS 電源畫面（能源 WP-9b；plan SPEC NRG-K1、K3，設計稿 reviews/energy-design-2026-10-21 k01 / k02 / k02b / k03）：
//   - UpsTab：網路與應用 ›「不斷電系統（UPS）N」分頁——每台 UPS 的狀態（● 市電 / ▲ 用電池 N 分鐘 / ✕ 輸出已斷 / ⊘ 讀不到，最後讀到的時間）、
//     電量、剩餘時間、負載、下游；讀不到的欄位寫「讀不到」不寫 0；用電池中的排前面（kmgr 排好）
//   - UpsDetail：點一台 → 讀數、UPS 回報的狀況、接在後面的設備；沒登記下游時頁頂提示〔登記下游〕
//   - DownstreamDialog：〔登記下游〕多選這個{leaf}的 Kinio 設備、主機、網路設備（整份覆寫，kmgr PUT …/ups/{probe}/downstream；最多 200 個）
// 資料：kmgr GET /v1/homes/{home}/kmgr/power（每個開了 IT 監控的{leaf}一次）；權限 kmgr.read，登記要 kmgr.configure（can_configure）。
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../../lib/scope';
import { stamp, timeStamp } from '../../../lib/timefmt';
import { Dialog } from '../../../components/Dialog';
import { SkeletonLines } from '../../../components/Skeleton';
import { kmgrApi } from '../api';
import { isNetworkDevice } from '../model';
import { errText, NS, StateBox } from '../parts';
import { powerApi, type HomePower, type UpsView } from '../power';
import type { HomeStatus } from '../types';

type TFn = (k: string, o?: Record<string, unknown>) => string;

/** 剩餘分鐘（無條件捨去）。 */
export function runtimeMin(s: number | undefined): number | undefined {
  return s === undefined ? undefined : Math.floor(s / 60);
}

/** 一台 UPS 的狀態：符號、等級、文字。 */
export function upsState(t: TFn, u: UpsView, now = Date.now()): { icon: string; cls: 'ok' | 'bad' | 'attention' | 'unreadable'; text: string } {
  if (!u.readable) return { icon: '⊘', cls: 'unreadable', text: t('power.st_unreadable', { time: timeStamp(u.at, undefined, false) }) };
  if (u.self_test) return { icon: '●', cls: 'ok', text: t('power.st_selftest') };
  switch (u.source) {
    case 'battery': {
      const min = u.on_battery_since ? Math.max(0, Math.floor((now - Date.parse(u.on_battery_since)) / 60_000)) : 0;
      // 轉電池 ≥ 5 分鐘或剩不到 15 分鐘 = 緊急（▲），之前是重要（◆）
      const urgent = min >= 5 || (u.runtime_s !== undefined && u.runtime_s < 15 * 60);
      return { icon: urgent ? '▲' : '◆', cls: urgent ? 'bad' : 'attention', text: t('power.st_battery', { n: min }) };
    }
    case 'none':
      return { icon: '▲', cls: 'bad', text: t('power.st_none') };
    case 'bypass':
      return { icon: '●', cls: 'attention', text: t('power.st_bypass') };
    case 'normal':
      return { icon: '●', cls: 'ok', text: t('power.st_normal') };
    default:
      return { icon: '●', cls: 'attention', text: t('power.st_other') };
  }
}

const unk = (t: TFn) => <span className="muted">{t('power.val_unk')}</span>;
const pct = (t: TFn, v: number | undefined) => (v === undefined ? unk(t) : `${Math.floor(v)}%`);
const mins = (t: TFn, s: number | undefined) => (s === undefined ? unk(t) : t('power.minutes', { n: runtimeMin(s) }));

export function UpsStatePill({ u }: { u: UpsView }) {
  const { t } = useTranslation(NS);
  const st = upsState(t, u);
  return (
    <span className={'km-st ' + st.cls} data-ups-state={u.readable ? u.source : 'unreadable'}>
      <span aria-hidden>{st.icon}</span> {st.text}
    </span>
  );
}

/** 範圍內開了 IT 監控的{leaf}的 UPS（每個{leaf}一次 GET …/kmgr/power）。 */
export function useHomesPower(items: HomeStatus[]) {
  const { homes, planes } = useScope();
  const apiOf = (id: string) => homes.find((x) => x.home_id === id)?.endpoints?.api ?? planes[0]?.api ?? '';
  return useQueries({
    queries: items.map((h) => ({
      queryKey: ['kmgr', 'power', 'home', h.home_id],
      queryFn: () => powerApi.home(apiOf(h.home_id), h.home_id),
      refetchInterval: 30_000,
      retry: false,
    })),
  });
}

export function UpsTab({ items, homeLabel, crossOrg }: { items: HomeStatus[]; homeLabel: (h: HomeStatus) => string; crossOrg: boolean }) {
  const { t } = useTranslation(NS);
  const [sp] = useSearchParams();
  const qs = useHomesPower(items);
  const rows = items.flatMap((h, i) => (qs[i]?.data?.ups ?? []).map((u) => ({ u, h, p: qs[i].data! })));
  const sel = sp.get('ups');
  const failed = qs.filter((q) => q.isError && !q.data);
  if (qs.some((q) => q.isLoading))
    return (
      <div className="card">
        <SkeletonLines n={3} label={t('common.loading')} />
      </div>
    );
  const cur = rows.find((r) => r.u.probe_id === sel && (!sp.get('home') || r.h.home_id === sp.get('home')));
  if (cur) return <UpsDetail u={cur.u} p={cur.p} h={cur.h} label={homeLabel(cur.h)} />;
  return (
    <>
      {failed.length > 0 && (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ⊘
          </span>
          <span className="txt">{t('power.load_err')}（{errText(t, failed[0].error)}）</span>
        </div>
      )}
      {!rows.length ? (
        <StateBox icon="○" title={t('net.tab_ups')} body={t('power.empty')} testId="km-ups-empty" />
      ) : (
        <div className="card">
          <table className="km-table" data-testid="km-ups-table">
            <thead>
              <tr>
                <th>{t('power.col_name')}</th>
                {items.length > 1 && <th>{crossOrg ? t('hosts.col_org_home') : t('hosts.col_home')}</th>}
                <th>{t('power.col_state')}</th>
                <th className="num">{t('power.col_battery')}</th>
                <th className="num">{t('power.col_runtime')}</th>
                <th className="num">{t('power.col_load')}</th>
                <th>{t('power.col_downstream')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ u, h }) => (
                <tr key={u.home_id + u.probe_id} data-ups={u.probe_id} className={u.readable ? undefined : 'km-stale'}>
                  <td>
                    <Link to={`/kmgr/network?tab=ups&home=${encodeURIComponent(h.home_id)}&ups=${encodeURIComponent(u.probe_id)}`}>
                      <strong>{u.name || u.probe_id}</strong>
                    </Link>
                  </td>
                  {items.length > 1 && <td>{homeLabel(h)}</td>}
                  <td>
                    <UpsStatePill u={u} />
                  </td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {pct(t, u.battery_pct)}
                  </td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {mins(t, u.runtime_s)}
                  </td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {pct(t, u.load_pct)}
                  </td>
                  <td>{u.downstream ? t('power.downstream_n', { n: u.downstream }) : <span className="km-metric attention">{t('power.downstream_none')}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function UpsDetail({ u, p, h, label }: { u: UpsView & { downstream_refs?: string[] }; p: HomePower; h: HomeStatus; label: string }) {
  const { t } = useTranslation(NS);
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const refs = u.downstream_refs ?? [];
  const nameOf = useNameOf(h.home_id, p);
  return (
    <section data-testid="km-ups-detail" data-ups={u.probe_id}>
      <p>
        <Link to="/kmgr/network?tab=ups">← {t('power.detail_back')}</Link>
      </p>
      <h2 className="section-title">
        {u.name || u.probe_id} <span className="muted km-sub">· {label}</span>
      </h2>
      {!u.downstream && (
        <div className="km-bar warn" role="note" data-testid="km-ups-todo">
          <span className="ic" aria-hidden>
            !
          </span>
          <span className="txt">{t('power.todo_hint')}</span>
          {p.can_configure && (
            <button type="button" className="btn btn-sm" onClick={() => setOpen(true)}>
              {t('power.register')}
            </button>
          )}
        </div>
      )}
      {saved && (
        <div className="km-bar ok" role="status">
          <span className="ic" aria-hidden>
            ✓
          </span>
          <span className="txt">{saved}</span>
        </div>
      )}
      {u.outage_incident_id && (
        <div className="km-bar bad" role="status">
          <span className="ic" aria-hidden>
            ▲
          </span>
          <span className="txt">{t('power.outage_open')}</span>
          <Link className="btn btn-sm" to={`/kmgr/incidents/${encodeURIComponent(u.home_id)}/${encodeURIComponent(u.outage_incident_id)}`}>
            {t('power.see_incident')}
          </Link>
        </div>
      )}
      <div className="card">
        <p style={{ marginTop: 0 }}>
          <UpsStatePill u={u} />
        </p>
        <dl className="km-kv">
          <dt>{t('power.col_battery')}</dt>
          <dd className="tabular">{pct(t, u.battery_pct)}</dd>
          <dt>{t('power.col_runtime')}</dt>
          <dd className="tabular">{mins(t, u.runtime_s)}</dd>
          <dt>{t('power.col_load')}</dt>
          <dd className="tabular">{pct(t, u.load_pct)}</dd>
          <dt>{t('power.output_w')}</dt>
          <dd className="tabular">{u.output_w === undefined ? unk(t) : `${Math.round(u.output_w)} W`}</dd>
          <dt>{t('power.input_v')}</dt>
          <dd className="tabular">{u.input_v === undefined ? unk(t) : `${Math.round(u.input_v)} V`}</dd>
          <dt>{t('power.temp')}</dt>
          <dd className="tabular">{u.battery_temp_c === undefined ? unk(t) : `${Math.round(u.battery_temp_c)} °C`}</dd>
          <dt>{t('power.test')}</dt>
          <dd>{u.test_result ? t(`power.test_${u.test_result}`) : unk(t)}</dd>
          <dt>{t('power.alarms')}</dt>
          <dd data-testid="km-ups-alarms">{u.alarms.length ? u.alarms.map((a) => t(`power.alarm.${a}`, { defaultValue: t('power.alarm.general_fault') })).join('、') : t('power.alarms_none')}</dd>
        </dl>
        <p className="muted km-sub" style={{ marginBottom: 0 }}>
          {stamp(u.at)}
        </p>
      </div>
      <div className="card" data-testid="km-ups-downstream">
        <div className="toolbar">
          <h3 className="section-title" style={{ margin: 0 }}>
            {t('power.downstream_title')}
          </h3>
          <span className="spacer" />
          {p.can_configure ? (
            <button type="button" className="btn btn-sm" onClick={() => setOpen(true)} data-testid="km-ups-register">
              {t('power.register')}
            </button>
          ) : (
            <span className="muted km-sub">{t('power.no_configure')}</span>
          )}
        </div>
        {refs.length ? (
          <ul className="plain-list">
            {refs.map((r) => (
              <li key={r}>{nameOf(r)}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t('power.no_downstream')}</p>
        )}
      </div>
      {open && (
        <DownstreamDialog
          u={u}
          p={p}
          homeId={h.home_id}
          close={() => setOpen(false)}
          done={(n) => {
            setOpen(false);
            setSaved(t('power.dlg_saved', { n }));
          }}
        />
      )}
    </section>
  );
}

/** 下游對象的名稱：Kinio 設備（範圍的我的設備 / kmgr names.things）、主機（KMA-）、網路設備（kpr-）。 */
function useNameOf(homeId: string, p: HomePower): (ref: string) => string {
  const { devices } = useScope();
  const { homes, planes } = useScope();
  const api = homes.find((x) => x.home_id === homeId)?.endpoints?.api ?? planes[0]?.api ?? '';
  const hosts = useQuery({ queryKey: ['kmgr', 'home-hosts', homeId], queryFn: () => kmgrApi.homeHosts(api, homeId), staleTime: 60_000 });
  const probes = useQuery({ queryKey: ['kmgr', 'probes', homeId], queryFn: () => kmgrApi.probes(api, homeId), staleTime: 60_000 });
  return (ref: string) =>
    p.names?.things?.[ref] ??
    devices.find((d) => d.device_ref === ref)?.display_name ??
    hosts.data?.find((x) => x.target_ref === ref)?.hostname ??
    probes.data?.find((x) => x.probe_id === ref)?.name ??
    ref;
}

interface Cand {
  ref: string;
  name: string;
  group: 'kinio' | 'hosts' | 'net';
}

function DownstreamDialog({ u, p, homeId, close, done }: { u: UpsView & { downstream_refs?: string[] }; p: HomePower; homeId: string; close: () => void; done: (n: number) => void }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const { devices, homes, planes } = useScope();
  const api = homes.find((x) => x.home_id === homeId)?.endpoints?.api ?? planes[0]?.api ?? '';
  const hosts = useQuery({ queryKey: ['kmgr', 'home-hosts', homeId], queryFn: () => kmgrApi.homeHosts(api, homeId), staleTime: 60_000 });
  const probes = useQuery({ queryKey: ['kmgr', 'probes', homeId], queryFn: () => kmgrApi.probes(api, homeId), staleTime: 60_000 });
  const upsIds = new Set(p.ups.map((x) => x.probe_id));
  const cands: Cand[] = useMemo(
    () => [
      ...devices.filter((d) => d.home_id === homeId).map((d): Cand => ({ ref: d.device_ref, name: d.display_name || d.device_ref, group: 'kinio' })),
      ...(hosts.data ?? []).map((x): Cand => ({ ref: x.target_ref, name: x.hostname || x.target_ref, group: 'hosts' })),
      ...(probes.data ?? []).filter((x) => isNetworkDevice(x) && !upsIds.has(x.probe_id)).map((x): Cand => ({ ref: x.probe_id, name: x.name || x.target || x.probe_id, group: 'net' })),
    ],
    [devices, homeId, hosts.data, probes.data], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [sel, setSel] = useState<Set<string>>(() => new Set(u.downstream_refs ?? []));
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const shown = cands.filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()));
  const toggle = (ref: string) => setSel((s) => (s.has(ref) ? new Set([...s].filter((x) => x !== ref)) : new Set([...s, ref])));
  const save = async () => {
    if (sel.size > 200) return setErr(t('power.dlg_max'));
    setBusy(true);
    setErr(null);
    try {
      await powerApi.putDownstream(api, homeId, u.probe_id, [...sel]);
      void qc.invalidateQueries({ queryKey: ['kmgr', 'power'] });
      void qc.invalidateQueries({ queryKey: ['kmgr', 'dashboard'] });
      done(sel.size);
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const groups: Cand['group'][] = ['kinio', 'hosts', 'net'];
  return (
    <Dialog open wide title={t('power.dlg_title', { name: u.name || u.probe_id })} onClose={close} testId="km-downstream">
      <p className="muted km-sub">{t('power.dlg_hint')}</p>
      <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('power.dlg_search')} aria-label={t('power.dlg_search')} />
      {!cands.length && !hosts.isLoading && !probes.isLoading && <p className="muted">{t('power.dlg_none')}</p>}
      <div style={{ maxHeight: 420, overflow: 'auto', marginTop: 8 }}>
        {groups.map((g) => {
          const list = shown.filter((c) => c.group === g);
          if (!list.length) return null;
          const all = list.every((c) => sel.has(c.ref));
          const label = t(`power.dlg_${g}`);
          return (
            <fieldset key={g} className="km-check" style={{ border: 0, padding: 0, margin: '8px 0' }} data-group={g}>
              <legend style={{ display: 'flex', gap: 12, alignItems: 'center', fontWeight: 600 }}>
                {label}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSel((s) => (all ? new Set([...s].filter((x) => !list.some((c) => c.ref === x))) : new Set([...s, ...list.map((c) => c.ref)])))}
                >
                  {t('power.dlg_all_group', { group: label })}
                </button>
              </legend>
              {list.map((c) => (
                <label key={c.ref} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 32 }} data-ref={c.ref}>
                  <input type="checkbox" checked={sel.has(c.ref)} onChange={() => toggle(c.ref)} />
                  {c.name}
                </label>
              ))}
            </fieldset>
          );
        })}
      </div>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      <div className="dialog-actions">
        <button type="button" className="btn" onClick={close}>
          {t('power.dlg_cancel')}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()} data-testid="km-downstream-save">
          {t('power.dlg_save', { n: sel.size })}
        </button>
      </div>
    </Dialog>
  );
}
